// Pure helpers behind the DML column pickers (SET assignments, INSERT column
// list): which table a statement writes to, that table's columns from the
// schema snapshot, and how a picked column is spelled in SQL. Kept out of the
// components so the lookups unit-test without React.

import type { SqlEngine } from '../../../../sql-grammar/model';
import { lookupRelationColumns } from '../../../../sql-resolver/service/snapshotRelations';
import type { FromItem, QueryStructure } from '../../../../sql-resolver/types/queryStructure';
import type { Column } from '../../../../sql-resolver/types/schema';
import type { VqeEditContext } from '../../edit/editContext';
import { formatIdentifierPart } from '../../edit/edits/changeDmlTarget';
import type { AssignmentListRouter } from '../../edit/edits/modifyColumnTargets';
import { isSameColumn, splitColumnRef } from '../../../../sql-resolver/scope/columnRefs';
import { safeExtractStructure } from '../insert/insertColumnMapHelpers';

/** The table a DML column list writes to. `columns` is null when the snapshot doesn't know them. */
export type WrittenTableColumns = {
  readonly tableName: string | null;
  readonly columns: readonly Column[] | null;
  /** The engine — column names compare by its identifier rules. */
  readonly engine: SqlEngine;
  /** Lower-cased names a column reference may be qualified with: the table and its alias. */
  readonly refNames: readonly string[];
};

/** The written table for a SET list (`router`) or, with `'insert'`, the INSERT target. */
function writtenItem(structure: QueryStructure, router: AssignmentListRouter | 'insert'): FromItem | undefined {
  if (router === 'insert' || router.conflictTail) return structure.insertTarget;
  if (router.mergeBranch) return structure.mergeTarget;
  const item = structure.updateTarget;
  // SQL Server `UPDATE p SET … FROM Production.Product p`: the target names a
  // FROM alias, so the columns are that FROM table's.
  if (item?.target.kind === 'relation' && !item.target.qualified.schema) {
    const folded = item.target.qualified.foldedName;
    const aliased = structure.fromItems?.find((from) => from.alias?.aliasFolded === folded);
    if (aliased) return aliased;
  }
  return item;
}

export function resolveWrittenTableColumns(
  editContext: VqeEditContext,
  router: AssignmentListRouter | 'insert',
): WrittenTableColumns {
  const engine = editContext.service.engineId;
  const structure = safeExtractStructure(editContext);
  const item = structure ? writtenItem(structure, router) : undefined;
  if (!item || item.target.kind !== 'relation') return unknownTable(engine);
  const qualified = item.target.qualified;
  const columns = lookupRelationColumns(editContext.service.getSchemaSnapshot(), qualified);
  return {
    tableName: qualified.name,
    columns: columns && columns.length > 0 ? columns : null,
    engine,
    refNames: [qualified.name, item.alias?.alias ?? ''].filter(Boolean).map((n) => n.toLowerCase()),
  };
}

export function unknownTable(engine: SqlEngine): WrittenTableColumns {
  return { tableName: null, columns: null, engine, refNames: [] };
}

/**
 * The table a (possibly qualified) reference writes to: `table` itself when
 * the reference is bare or qualified by the table / its alias, else unknown.
 * MySQL `UPDATE a JOIN b … SET b.flag = 1` writes `b`, whose columns the
 * structure doesn't carry — so no column list and no "not a column" mark.
 */
export function tableForRef(table: WrittenTableColumns, ref: string): WrittenTableColumns {
  const qualifier = splitColumnRef(ref).qualifier;
  if (!qualifier) return table;
  const owner = splitColumnRef(qualifier).name.toLowerCase();
  return table.refNames.includes(owner) ? table : unknownTable(table.engine);
}

/** The catalog column a written reference names, if the table has it. */
export function findColumn(table: WrittenTableColumns, ref: string): Column | undefined {
  return table.columns?.find((c) => isSameColumn(ref, c.qualified.name, table.engine));
}

/**
 * The qualifier every existing target shares (`t` in `t.a = …, t.b = …`), or
 * null. A new pick follows the statement's own style: SQL Server MERGE writes
 * `t.qty`, PostgreSQL rejects a qualified SET target, and a statement already
 * written one way is one the engine accepts.
 */
export function sharedQualifier(refs: readonly string[]): string | null {
  if (refs.length === 0) return null;
  const first = splitColumnRef(refs[0]).qualifier;
  if (!first) return null;
  return refs.every((ref) => splitColumnRef(ref).qualifier === first) ? first : null;
}

/** SQL text for a catalog column: delimited per engine when needed, prefixed with `qualifier`. */
export function columnSqlText(column: Column, engine: SqlEngine, qualifier: string | null): string {
  const name = formatIdentifierPart(column.qualified.name, engine);
  return qualifier ? `${qualifier}.${name}` : name;
}
